import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type DateFilterAndOr = 'and' | 'or';
export type DateFilterField = 'start' | 'end' | 'reReleaseStart' | 'reReleaseEnd';
export type DateFilterOperator = 'blank' | 'notBlank' | 'equals' | 'greaterThan' | 'lessThan' | 'notEqual' | 'inRange';

export interface DateFilterRow {
  id: string;
  /** null on the first row — there's nothing before it to chain to. */
  andOr: DateFilterAndOr | null;
  field: DateFilterField;
  operator: DateFilterOperator;
}

/**
 * Pre-filled the first time the Dates filter loads. There's no filter-persistence service yet
 * (separate ticket) — until saved filters can be loaded, every session starts from this
 * product-specified default: (Start is Not Blank AND End is Blank) OR (Rerelease Start is Not
 * Blank AND Rerelease End is Blank).
 *
 * The ticket's prose calls this "two rows", but each row here only carries one field/operator
 * pair (per the acceptance criteria's row shape), so the 4-condition boolean expression above
 * can only be represented as 4 rows — reading the "two rows" wording as "two AND-grouped
 * clauses" rather than two literal UI rows. Standard AND-before-OR precedence on this flat
 * [none, and, or, and] chain reproduces the parenthesized expression exactly. Worth confirming
 * with product if a literal 2-row (compound-condition) UI was actually intended instead.
 */
const DEFAULT_DATE_FILTER_ROWS: ReadonlyArray<Omit<DateFilterRow, 'id'>> = [
  { andOr: null, field: 'start', operator: 'notBlank' },
  { andOr: 'and', field: 'end', operator: 'blank' },
  { andOr: 'or', field: 'reReleaseStart', operator: 'notBlank' },
  { andOr: 'and', field: 'reReleaseEnd', operator: 'blank' },
];

let nextRowId = 0;
function createRowId(): string {
  nextRowId += 1;
  return `date-filter-row-${nextRowId}`;
}

export function buildDefaultDateFilterRows(): DateFilterRow[] {
  return DEFAULT_DATE_FILTER_ROWS.map((row) => ({ ...row, id: createRowId() }));
}

/**
 * Real state behind the Dates filter section: `rows$` is the single source of truth, written to
 * by PrizeDateFilterComponent and read by both the component (to render the condition rows) and
 * the "must have a Date filter" submit guard in GeneratePrizesPanelComponent (via
 * hasAtLeastOneCondition() below).
 */
@Injectable({
  providedIn: 'root',
})
export class PrizeDateFilterStateService {
  private readonly rows$ = new BehaviorSubject<DateFilterRow[]>(buildDefaultDateFilterRows());

  getRows(): DateFilterRow[] {
    return this.rows$.value;
  }

  watchRows(): Observable<DateFilterRow[]> {
    return this.rows$.asObservable();
  }

  setRows(rows: DateFilterRow[]): void {
    this.rows$.next(rows);
  }

  hasAtLeastOneCondition(): boolean {
    return this.rows$.value.length > 0;
  }
}
